import { useEffect, useRef, useCallback } from "react";
import { clearPassword } from "@/lib/api/auth";

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
];

// Throttle interval — don't reset timer more than once per 60s
const THROTTLE_MS = 60_000;

/**
 * Logs the staff user out after 1 hour of inactivity.
 * Pharmacy session stays intact — user is redirected to /select-user.
 *
 * "Activity" = mousedown, keydown, touchstart, or scroll.
 * Timer resets on each activity event (throttled to avoid perf overhead).
 */
export function useIdleTimeout(enabled: boolean) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef(Date.now());
  const isEndingRef = useRef(false);

  const endSession = useCallback(async () => {
    if (isEndingRef.current) return;
    isEndingRef.current = true;
    try {
      await clearPassword();
    } catch {
      // Best effort
    }
    sessionStorage.removeItem("activeUser");
    window.location.replace("/select-user");
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(endSession, IDLE_TIMEOUT_MS);
  }, [endSession]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current < THROTTLE_MS) return;
    lastActivityRef.current = now;
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) return;

    // Check if already idle on mount (e.g. tab was backgrounded and came back)
    resetTimer();

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    // Also reset on visibility change — if tab was hidden and comes back,
    // check how long it was hidden
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= IDLE_TIMEOUT_MS) {
          endSession();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, handleActivity, resetTimer, endSession]);
}
